import React, { createContext, useContext, useState } from 'react'

export type Language = 'fr' | 'en' | 'de'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const translations = {
  fr: {
    // Navigation
    'nav.stats': 'Chiffres clés',
    'nav.services': 'Nos expertises',
    'nav.process': 'Processus',
    'nav.projects': 'Projets',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contact',
    'nav.launchProject': 'Lancer un projet',
    'nav.joinTeam': 'Rejoindre l\'équipe',
    
    // Hero Section
    'hero.title': 'Des étudiants experts en',
    'hero.subtitle': 'Nous transformons vos idées en prototypes et livrables réels.',
    'hero.subtitle2': 'Rapidement. De manière transparente.',
    'hero.words.software': 'Logiciel',
    'hero.words.ai': 'IA',
    'hero.words.robotics': 'Robotique',
    'hero.words.agriculture': 'Agronomie',
    'hero.words.architecture': 'Architecture',
    'hero.words.mechanical': 'Génie mécanique',
    
    // Stats Section
    'stats.title': 'Des résultats qui parlent',
    'stats.subtitle': 'Notre expertise reconnue par nos partenaires et nos résultats.',
    'stats.students': 'étudiants motivés',
    'stats.domains': 'domaines d\'expertise',
    'stats.contact': 'premier contact-brief',

    // Services Section
    'services.title': 'Nos expertises',
    'services.subtitle': 'De l\'idée au prototype, notre équipe pluridisciplinaire transforme vos besoins en solutions concrètes.',
    'services.computer': 'Informatique',
    'services.computer.desc': 'Développement de logiciels, design de cartes, montages.',
    'services.agronomy': 'Agronomie', 
    'services.agronomy.desc': 'SIG, planification de cultures, relevés terrain.',
    'services.mechanical': 'Génie mécanique',
    'services.mechanical.desc': 'Plans de machineries, production de pièces, études de projet.',
    'services.microtechnics': 'Microtechnique',
    'services.microtechnics.desc': 'Conception de cartes, dessins de plans, prototypage.',
    'services.architecture': 'Architecture',
    'services.architecture.desc': 'Plans, design, études de projets.',
    'services.civil': 'Ingénierie civile',
    'services.civil.desc': 'Calculs, dessins, conseils.',
    'services.nature': 'Gestion de la nature',
    'services.nature.desc': 'Études de zones et biotopes, inventaires d\'espèces, revitalisation.',
    'services.landscape': 'Architecture du paysage',
    'services.landscape.desc': 'Design d\'espaces extérieurs, plans, rénovations.',
    'services.building': 'Technique du bâtiment',
    'services.building.desc': 'Dessins, conseils, dimensionnement.',
    'services.request': 'Demander ce service',

    // Process Section
    'process.title': 'Processus',
    'process.subtitle': 'Une méthode éprouvée en 4 étapes pour garantir la réussite de votre projet.',
    'process.brief': 'Brief',
    'process.brief.desc': 'Vous partagez vos objectifs et contraintes.',
    'process.match': 'Match',
    'process.match.desc': 'Nous sélectionnons les bons étudiants et un encadrant.',
    'process.realization': 'Réalisation',
    'process.realization.desc': 'Livraisons hebdo. Suivi clair. Versions maîtrisées.',
    'process.review': 'Revue',
    'process.review.desc': 'Démo, documentation, remise finale. Option d\'itération.',

    // Projects Section
    'projects.title': 'Projets',
    'projects.subtitle': 'Découvrez comment nous avons transformé des idées en solutions concrètes.',
    'projects.problem': 'Problème',
    'projects.approach': 'Approche',
    'projects.result': 'Résultat',

    // Projects Section - Content
    'projects.weather.title': 'Collecte de données météo pour l\'agriculture',
    'projects.weather.category': 'Agronomie',
    'projects.weather.problem': 'Un exploitant agricole avait besoin d\'optimiser l\'irrigation de ses cultures selon les conditions météorologiques locales.',
    'projects.weather.approach': 'Déploiement de capteurs IoT (température, humidité, pluviométrie) connectés via LoRaWAN, développement d\'un tableau de bord web avec alertes automatisées.',
    'projects.weather.result': 'Système opérationnel sur 15 hectares, réduction de 30% de la consommation d\'eau avec amélioration du rendement.',
    'projects.weather.metrics': '8 semaines • 15 ha • -30% eau',

    'projects.mvp.title': 'MVP outil de gestion interne',
    'projects.mvp.category': 'Informatique',
    'projects.mvp.problem': 'Une startup tech avait besoin d\'un outil de gestion de projet collaboratif adapté à ses processus spécifiques.',
    'projects.mvp.approach': 'Développement agile d\'une webapp React avec API Node.js, intégration des outils existants et interface utilisateur intuitive.',
    'projects.mvp.result': 'Plateforme déployée en production, adoption par 50+ utilisateurs avec amélioration significative de la productivité équipe.',
    'projects.mvp.metrics': '4 semaines • 50+ utilisateurs',

    'projects.automation.title': 'Système d\'automatisation de rapports',
    'projects.automation.category': 'Génie mécanique',
    'projects.automation.problem': 'Bureau d\'études devait automatiser la génération de rapports techniques chronophages pour les projets mécaniques.',
    'projects.automation.approach': 'Pipeline de traitement automatisé avec templates CAO, extraction de données techniques et génération de documentation standardisée.',
    'projects.automation.result': 'Réduction de 85% du temps de génération des rapports, standardisation complète des livrables clients.',
    'projects.automation.metrics': '10 semaines • -85% temps',

    // Guarantees Section
    'guarantees.title': 'Garanties',
    'guarantees.subtitle': 'Nous nous engageons sur la qualité, les délais et la transparence de nos prestations.',
    'guarantees.quality': 'Qualité',
    'guarantees.quality.desc': 'Encadrement par enseignants si besoin.',
    'guarantees.custom': 'Sur-mesure',
    'guarantees.custom.desc': 'Adapté au budget et délais.',
    'guarantees.professionalism': 'Professionnalisme',
    'guarantees.professionalism.desc': 'Communication claire. Suivi hebdo. Versions contrôlées.',
    'guarantees.ip': 'Propriété intellectuelle',
    'guarantees.ip.desc': 'Transfert complet après paiement.',

    // FAQ Section
    'faq.title': 'FAQ',
    'faq.subtitle': 'Les réponses aux questions les plus fréquentes sur nos services.',
    'faq.q1': 'Quels sont vos tarifs ?',
    'faq.a1': 'Nos tarifs sont adaptés aux budgets étudiants : forfait projet ou facturation au temps passé. Nous proposons un devis clair et détaillé avant chaque mission.',
    'faq.q2': 'Quels sont les délais typiques ?',
    'faq.a2': 'Pour un MVP : 2 à 6 semaines selon la complexité. Les grands projets sont découpés en phases pour garantir des livraisons régulières et un suivi optimal.',
    'faq.q3': 'Qui travaille sur mon projet ?',
    'faq.a3': 'Des étudiants sélectionnés de HEPIA selon vos besoins techniques. Chaque projet dispose d\'un chef de projet et d\'un encadrant enseignant si nécessaire.',
    'faq.q4': 'Gérez-vous les NDA et la propriété intellectuelle ?',
    'faq.a4': 'Oui, nous signons des accords de confidentialité standards. La propriété intellectuelle est intégralement transférée au client après paiement final.',
    'faq.q5': 'Proposez-vous un suivi post-projet ?',
    'faq.a5': 'Absolument. Nous offrons un support de maintenance et la possibilité de nouvelles phases d\'évolution selon vos besoins futurs.',

    // Contact Section
    'contact.title': 'Lancez votre projet',
    'contact.subtitle': 'Un projet en tête ? Nous le cadrons cette semaine.',
    'contact.name': 'Nom',
    'contact.firstname': 'Prénom',
    'contact.email': 'Email',
    'contact.phone': 'Téléphone',
    'contact.organization': 'Organisation',
    'contact.budget': 'Budget estimé',
    'contact.budget.placeholder': 'Sélectionnez votre budget',
    'contact.budget.small': 'Moins de 2k€',
    'contact.budget.medium': '2k€ - 10k€',
    'contact.budget.large': '10k€ - 25k€',
    'contact.budget.enterprise': 'Plus de 25k€',
    'contact.budget.discuss': 'À discuter',
    'contact.services.selected': 'Services sélectionnés',
    'contact.message': 'Message',
    'contact.message.note': '(les services choisis sont ajoutés ci-dessus)',
    'contact.send': 'Envoyer le message',
    'contact.info.title': 'Informations de contact',
    'contact.info.email': 'Email',
    'contact.info.address': 'Adresse',
    'contact.social': 'Suivez-nous',
    'contact.student.title': 'Vous êtes étudiant ?',
    'contact.student.subtitle': 'Rejoignez notre équipe de consultants et participez à des projets concrets.',
    'contact.student.button': 'Rejoindre comme consultant étudiant',

    // Footer
    'footer.description': 'Des étudiants experts qui transforment vos idées en livrables concrets.',
    'footer.services': 'Services',
    'footer.process': 'Processus',
    'footer.projects': 'Projets',
    'footer.contact': 'Contact',
    'footer.follow': 'Suivez-nous sur',
    'footer.copyright': 'Junior Entreprise HEPIA. Tous droits réservés.',
    'footer.legal': 'Mentions légales',
    'footer.privacy': 'Politique de confidentialité',
    'footer.terms': 'CGV',

    // Rejoindre Page
    'join.hero.title': 'Rejoignez notre équipe de consultants étudiants',
    'join.hero.subtitle': 'Vous êtes étudiant à HEPIA et souhaitez mettre vos compétences au service de projets concrets ? Rejoignez-nous et développez votre expérience professionnelle.',
    'join.form.title': 'Formulaire de candidature',
    'join.form.subtitle': 'Complétez les informations ci-dessous pour soumettre votre candidature',
    'join.field': 'Filière / Section',
    'join.field.placeholder': 'Sélectionnez votre filière',
    'join.motivation': 'Message de motivation',
    'join.motivation.placeholder': 'Expliquez pourquoi vous souhaitez rejoindre notre équipe...',
    'join.motivation.min': 'Minimum 50 caractères',
    'join.cv': 'CV (PDF uniquement)',
    'join.cv.upload': 'Téléchargez votre CV',
    'join.cv.change': 'Cliquez pour changer de fichier',
    'join.cv.format': 'Format PDF uniquement, max 5MB',
    'join.privacy.title': 'Confidentialité des données',
    'join.privacy.text': 'Vos données personnelles sont traitées conformément au RGPD. Elles seront utilisées uniquement dans le cadre du processus de recrutement et ne seront pas transmises à des tiers. Vous disposez d\'un droit d\'accès, de rectification et de suppression de vos données.',
    'join.submit': 'Soumettre ma candidature',
    'join.submitting': 'Envoi en cours...',
    'join.success.title': 'Candidature envoyée avec succès !',
    'join.success.subtitle': 'Merci pour votre intérêt à rejoindre la Junior Entreprise HEPIA. Nous examinerons votre candidature et vous contacterons dans les 72 heures.',
    'join.success.button': 'Retour à l\'accueil',

    'join.sections.computer': 'Informatique et Ingénierie Logicielle',
    'join.sections.electronics': 'Électronique',
    'join.sections.microtechnics': 'Microtechniques',
    'join.sections.mechanical': 'Génie Mécanique',
    'join.sections.architecture': 'Architecture',
    'join.sections.civil': 'Génie Civil',
    'join.sections.other': 'Autre filière',

    // Form validation messages
    'join.validation.section': 'Veuillez sélectionner votre filière.',
    'join.validation.name.min': 'Le nom doit contenir au moins 2 caractères.',
    'join.validation.firstname.min': 'Le prénom doit contenir au moins 2 caractères.',
    'join.validation.phone.min': 'Veuillez saisir un numéro de téléphone valide.',
    'join.validation.motivation.min': 'Votre message de motivation doit contenir au moins 50 caractères.',
    'join.error.pdf': 'Veuillez sélectionner un fichier PDF uniquement.',
    'join.error.size': 'Le fichier ne doit pas dépasser 5MB.',
    'join.error.cv.required': 'Veuillez télécharger votre CV au format PDF.',
    'join.success.prepared': 'Candidature préparée !',
    'join.success.email.instruction': 'Votre client email va s\'ouvrir. N\'oubliez pas de joindre votre CV.',
    'join.error.general': 'Une erreur est survenue lors de l\'envoi. Veuillez réessayer.',
  },
  en: {
    // Navigation
    'nav.stats': 'Key figures',
    'nav.services': 'Our expertise',
    'nav.process': 'Process',
    'nav.projects': 'Projects',
    'nav.faq': 'FAQ',
    'nav.contact': 'Contact',
    'nav.launchProject': 'Launch a project',
    'nav.joinTeam': 'Join the team',
    
    // Hero Section
    'hero.title': 'Student experts in',
    'hero.subtitle': 'We transform your ideas into real prototypes and deliverables.',
    'hero.subtitle2': 'Quickly. Transparently.',
    'hero.words.software': 'Software',
    'hero.words.ai': 'AI',
    'hero.words.robotics': 'Robotics',
    'hero.words.agriculture': 'Agronomy',
    'hero.words.architecture': 'Architecture',
    'hero.words.mechanical': 'Mechanical Engineering',
    
    // Stats Section
    'stats.title': 'Results that speak',
    'stats.subtitle': 'Our expertise recognized by our partners and our results.',
    'stats.students': 'motivated students',
    'stats.domains': 'domains of expertise',
    'stats.contact': 'first contact-brief',

    // Services Section
    'services.title': 'Our expertise',
    'services.subtitle': 'From idea to prototype, our multidisciplinary team transforms your needs into concrete solutions.',
    'services.computer': 'Computer Science',
    'services.computer.desc': 'Software development, card design, assemblies.',
    'services.agronomy': 'Agronomy',
    'services.agronomy.desc': 'GIS, crop planning, field surveys.',
    'services.mechanical': 'Mechanical Engineering',
    'services.mechanical.desc': 'Machinery plans, parts production, project studies.',
    'services.microtechnics': 'Microtechnics',
    'services.microtechnics.desc': 'Card design, plan drawings, prototyping.',
    'services.architecture': 'Architecture',
    'services.architecture.desc': 'Plans, design, project studies.',
    'services.civil': 'Civil Engineering',
    'services.civil.desc': 'Calculations, drawings, advice.',
    'services.nature': 'Nature Management',
    'services.nature.desc': 'Zone and biotope studies, species inventories, revitalization.',
    'services.landscape': 'Landscape Architecture',
    'services.landscape.desc': 'Outdoor space design, plans, renovations.',
    'services.building': 'Building Technology',
    'services.building.desc': 'Drawings, advice, dimensioning.',
    'services.request': 'Request this service',

    // Process Section
    'process.title': 'Process',
    'process.subtitle': 'A proven 4-step method to guarantee your project\'s success.',
    'process.brief': 'Brief',
    'process.brief.desc': 'You share your objectives and constraints.',
    'process.match': 'Match',
    'process.match.desc': 'We select the right students and a supervisor.',
    'process.realization': 'Realization',
    'process.realization.desc': 'Weekly deliveries. Clear monitoring. Controlled versions.',
    'process.review': 'Review',
    'process.review.desc': 'Demo, documentation, final delivery. Iteration option.',

    // Projects Section
    'projects.title': 'Projects',
    'projects.subtitle': 'Discover how we transformed ideas into concrete solutions.',
    'projects.problem': 'Problem',
    'projects.approach': 'Approach',
    'projects.result': 'Result',

    // Projects Section - Content
    'projects.weather.title': 'Weather data collection for agriculture',
    'projects.weather.category': 'Agronomy',
    'projects.weather.problem': 'An agricultural operator needed to optimize irrigation of their crops according to local weather conditions.',
    'projects.weather.approach': 'Deployment of IoT sensors (temperature, humidity, rainfall) connected via LoRaWAN, development of a web dashboard with automated alerts.',
    'projects.weather.result': 'Operational system over 15 hectares, 30% reduction in water consumption with improved yield.',
    'projects.weather.metrics': '8 weeks • 15 ha • -30% water',

    'projects.mvp.title': 'Internal management tool MVP',
    'projects.mvp.category': 'Computer Science',
    'projects.mvp.problem': 'A tech startup needed a collaborative project management tool adapted to their specific processes.',
    'projects.mvp.approach': 'Agile development of a React webapp with Node.js API, integration of existing tools and intuitive user interface.',
    'projects.mvp.result': 'Platform deployed in production, adoption by 50+ users with significant improvement in team productivity.',
    'projects.mvp.metrics': '4 weeks • 50+ users',

    'projects.automation.title': 'Report automation system',
    'projects.automation.category': 'Mechanical Engineering',
    'projects.automation.problem': 'Design office had to automate the generation of time-consuming technical reports for mechanical projects.',
    'projects.automation.approach': 'Automated processing pipeline with CAD templates, technical data extraction and standardized documentation generation.',
    'projects.automation.result': '85% reduction in report generation time, complete standardization of client deliverables.',
    'projects.automation.metrics': '10 weeks • -85% time',

    // Guarantees Section
    'guarantees.title': 'Guarantees',
    'guarantees.subtitle': 'We commit to quality, deadlines and transparency of our services.',
    'guarantees.quality': 'Quality',
    'guarantees.quality.desc': 'Teacher supervision if needed.',
    'guarantees.custom': 'Custom-made',
    'guarantees.custom.desc': 'Adapted to budget and deadlines.',
    'guarantees.professionalism': 'Professionalism',
    'guarantees.professionalism.desc': 'Clear communication. Weekly follow-up. Controlled versions.',
    'guarantees.ip': 'Intellectual property',
    'guarantees.ip.desc': 'Complete transfer after payment.',

    // FAQ Section
    'faq.title': 'FAQ',
    'faq.subtitle': 'Answers to the most frequently asked questions about our services.',
    'faq.q1': 'What are your rates?',
    'faq.a1': 'Our rates are adapted to student budgets: project package or time-based billing. We provide a clear and detailed quote before each mission.',
    'faq.q2': 'What are typical deadlines?',
    'faq.a2': 'For an MVP: 2 to 6 weeks depending on complexity. Large projects are broken down into phases to guarantee regular deliveries and optimal monitoring.',
    'faq.q3': 'Who works on my project?',
    'faq.a3': 'Selected HEPIA students according to your technical needs. Each project has a project manager and a teaching supervisor if necessary.',
    'faq.q4': 'Do you handle NDAs and intellectual property?',
    'faq.a4': 'Yes, we sign standard confidentiality agreements. Intellectual property is fully transferred to the client after final payment.',
    'faq.q5': 'Do you offer post-project support?',
    'faq.a5': 'Absolutely. We offer maintenance support and the possibility of new evolution phases according to your future needs.',

    // Contact Section
    'contact.title': 'Launch your project',
    'contact.subtitle': 'Have a project in mind? We\'ll frame it this week.',
    'contact.name': 'Last name',
    'contact.firstname': 'First name',
    'contact.email': 'Email',
    'contact.phone': 'Phone',
    'contact.organization': 'Organization',
    'contact.budget': 'Estimated budget',
    'contact.budget.placeholder': 'Select your budget',
    'contact.budget.small': 'Less than 2k€',
    'contact.budget.medium': '2k€ - 10k€',
    'contact.budget.large': '10k€ - 25k€',
    'contact.budget.enterprise': 'More than 25k€',
    'contact.budget.discuss': 'To discuss',
    'contact.services.selected': 'Selected services',
    'contact.message': 'Message',
    'contact.message.note': '(chosen services are added above)',
    'contact.send': 'Send message',
    'contact.info.title': 'Contact information',
    'contact.info.email': 'Email',
    'contact.info.address': 'Address',
    'contact.social': 'Follow us',
    'contact.student.title': 'Are you a student?',
    'contact.student.subtitle': 'Join our team of consultants and participate in concrete projects.',
    'contact.student.button': 'Join as student consultant',

    // Footer
    'footer.description': 'Expert students who transform your ideas into concrete deliverables.',
    'footer.services': 'Services',
    'footer.process': 'Process',
    'footer.projects': 'Projects',
    'footer.contact': 'Contact',
    'footer.follow': 'Follow us on',
    'footer.copyright': 'Junior Enterprise HEPIA. All rights reserved.',
    'footer.legal': 'Legal mentions',
    'footer.privacy': 'Privacy policy',
    'footer.terms': 'Terms',

    // Rejoindre Page
    'join.hero.title': 'Join our team of student consultants',
    'join.hero.subtitle': 'Are you a HEPIA student and want to put your skills at the service of concrete projects? Join us and develop your professional experience.',
    'join.form.title': 'Application form',
    'join.form.subtitle': 'Fill in the information below to submit your application',
    'join.field': 'Field / Section',
    'join.field.placeholder': 'Select your field',
    'join.motivation': 'Motivation message',
    'join.motivation.placeholder': 'Explain why you want to join our team...',
    'join.motivation.min': 'Minimum 50 characters',
    'join.cv': 'CV (PDF only)',
    'join.cv.upload': 'Upload your CV',
    'join.cv.change': 'Click to change file',
    'join.cv.format': 'PDF format only, max 5MB',
    'join.privacy.title': 'Data confidentiality',
    'join.privacy.text': 'Your personal data is processed in accordance with GDPR. It will be used only for recruitment purposes and will not be transmitted to third parties. You have the right to access, rectify and delete your data.',
    'join.submit': 'Submit my application',
    'join.submitting': 'Sending...',
    'join.success.title': 'Application sent successfully!',
    'join.success.subtitle': 'Thank you for your interest in joining Junior Enterprise HEPIA. We will review your application and contact you within 72 hours.',
    'join.success.button': 'Back to home',

    'join.sections.computer': 'Computer Science and Software Engineering',
    'join.sections.electronics': 'Electronics',
    'join.sections.microtechnics': 'Microtechnics',
    'join.sections.mechanical': 'Mechanical Engineering',
    'join.sections.architecture': 'Architecture',
    'join.sections.civil': 'Civil Engineering',
    'join.sections.other': 'Other field',

    // Form validation messages
    'join.validation.section': 'Please select your field.',
    'join.validation.name.min': 'The name must contain at least 2 characters.',
    'join.validation.firstname.min': 'The first name must contain at least 2 characters.',
    'join.validation.phone.min': 'Please enter a valid phone number.',
    'join.validation.motivation.min': 'Your motivation message must contain at least 50 characters.',
    'join.error.pdf': 'Please select a PDF file only.',
    'join.error.size': 'The file must not exceed 5MB.',
    'join.error.cv.required': 'Please upload your CV in PDF format.',
    'join.success.prepared': 'Application prepared!',
    'join.success.email.instruction': 'Your email client will open. Don\'t forget to attach your CV.',
    'join.error.general': 'An error occurred while sending. Please try again.',
  },
  de: {
    // Navigation
    'nav.stats': 'Schlüsselzahlen',
    'nav.services': 'Unsere Expertise',
    'nav.process': 'Prozess',
    'nav.projects': 'Projekte',
    'nav.faq': 'FAQ',
    'nav.contact': 'Kontakt',
    'nav.launchProject': 'Projekt starten',
    'nav.joinTeam': 'Dem Team beitreten',
    
    // Hero Section
    'hero.title': 'Studentische Experten für',
    'hero.subtitle': 'Wir verwandeln Ihre Ideen in echte Prototypen und Ergebnisse.',
    'hero.subtitle2': 'Schnell. Transparent.',
    'hero.words.software': 'Software',
    'hero.words.ai': 'KI',
    'hero.words.robotics': 'Robotik',
    'hero.words.agriculture': 'Agronomie',
    'hero.words.architecture': 'Architektur',
    'hero.words.mechanical': 'Maschinenbau',
    
    // Stats Section
    'stats.title': 'Ergebnisse, die sprechen',
    'stats.subtitle': 'Unsere Expertise wird von unseren Partnern und unseren Ergebnissen anerkannt.',
    'stats.students': 'motivierte Studenten',
    'stats.domains': 'Fachbereiche',
    'stats.contact': 'erster Kontakt-Briefing',

    // Services Section
    'services.title': 'Unsere Expertise',
    'services.subtitle': 'Von der Idee zum Prototyp verwandelt unser multidisziplinäres Team Ihre Bedürfnisse in konkrete Lösungen.',
    'services.computer': 'Informatik',
    'services.computer.desc': 'Softwareentwicklung, Kartendesign, Montage.',
    'services.agronomy': 'Agronomie',
    'services.agronomy.desc': 'GIS, Anbauplanung, Felduntersuchungen.',
    'services.mechanical': 'Maschinenbau',
    'services.mechanical.desc': 'Maschinenpläne, Teileproduktion, Projektstudien.',
    'services.microtechnics': 'Mikrotechnik',
    'services.microtechnics.desc': 'Kartendesign, Planzeichnungen, Prototyping.',
    'services.architecture': 'Architektur',
    'services.architecture.desc': 'Pläne, Design, Projektstudien.',
    'services.civil': 'Bauingenieurwesen',
    'services.civil.desc': 'Berechnungen, Zeichnungen, Beratung.',
    'services.nature': 'Naturmanagement',
    'services.nature.desc': 'Zonen- und Biotopstudien, Artinventare, Revitalisierung.',
    'services.landscape': 'Landschaftsarchitektur',
    'services.landscape.desc': 'Außenraumgestaltung, Pläne, Renovierungen.',
    'services.building': 'Gebäudetechnik',
    'services.building.desc': 'Zeichnungen, Beratung, Dimensionierung.',
    'services.request': 'Diesen Service anfordern',

    // Process Section
    'process.title': 'Prozess',
    'process.subtitle': 'Eine bewährte 4-Schritte-Methode, um den Erfolg Ihres Projekts zu garantieren.',
    'process.brief': 'Briefing',
    'process.brief.desc': 'Sie teilen Ihre Ziele und Einschränkungen mit.',
    'process.match': 'Matching',
    'process.match.desc': 'Wir wählen die richtigen Studenten und einen Betreuer aus.',
    'process.realization': 'Realisierung',
    'process.realization.desc': 'Wöchentliche Lieferungen. Klare Überwachung. Kontrollierte Versionen.',
    'process.review': 'Review',
    'process.review.desc': 'Demo, Dokumentation, finale Lieferung. Iterationsmöglichkeit.',

    // Projects Section
    'projects.title': 'Projekte',
    'projects.subtitle': 'Entdecken Sie, wie wir Ideen in konkrete Lösungen verwandelt haben.',
    'projects.problem': 'Problem',
    'projects.approach': 'Ansatz',
    'projects.result': 'Ergebnis',

    // Projects Section - Content
    'projects.weather.title': 'Wetterdatensammlung für die Landwirtschaft',
    'projects.weather.category': 'Agronomie',
    'projects.weather.problem': 'Ein landwirtschaftlicher Betreiber musste die Bewässerung seiner Kulturen entsprechend den lokalen Wetterbedingungen optimieren.',
    'projects.weather.approach': 'Einsatz von IoT-Sensoren (Temperatur, Feuchtigkeit, Niederschlag) über LoRaWAN verbunden, Entwicklung eines Web-Dashboards mit automatisierten Warnungen.',
    'projects.weather.result': 'Operatives System auf 15 Hektar, 30% Reduzierung des Wasserverbrauchs bei verbessertem Ertrag.',
    'projects.weather.metrics': '8 Wochen • 15 ha • -30% Wasser',

    'projects.mvp.title': 'Internes Management-Tool MVP',
    'projects.mvp.category': 'Informatik',
    'projects.mvp.problem': 'Ein Tech-Startup benötigte ein kollaboratives Projektmanagement-Tool, das an ihre spezifischen Prozesse angepasst war.',
    'projects.mvp.approach': 'Agile Entwicklung einer React-Webapp mit Node.js-API, Integration bestehender Tools und intuitive Benutzeroberfläche.',
    'projects.mvp.result': 'Plattform in Produktion eingesetzt, Adoption durch 50+ Nutzer mit signifikanter Verbesserung der Teamproduktivität.',
    'projects.mvp.metrics': '4 Wochen • 50+ Nutzer',

    'projects.automation.title': 'Berichtsautomatisierungssystem',
    'projects.automation.category': 'Maschinenbau',
    'projects.automation.problem': 'Ein Konstruktionsbüro musste die Generierung zeitaufwändiger technischer Berichte für Maschinenprojekte automatisieren.',
    'projects.automation.approach': 'Automatisierte Verarbeitungspipeline mit CAD-Templates, technische Datenextraktion und standardisierte Dokumentationsgenerierung.',
    'projects.automation.result': '85% Reduzierung der Berichtsgenerierungszeit, vollständige Standardisierung der Kundenergebnisse.',
    'projects.automation.metrics': '10 Wochen • -85% Zeit',

    // Guarantees Section
    'guarantees.title': 'Garantien',
    'guarantees.subtitle': 'Wir verpflichten uns zu Qualität, Terminen und Transparenz unserer Dienstleistungen.',
    'guarantees.quality': 'Qualität',
    'guarantees.quality.desc': 'Lehrerbetreuung bei Bedarf.',
    'guarantees.custom': 'Maßgeschneidert',
    'guarantees.custom.desc': 'Angepasst an Budget und Termine.',
    'guarantees.professionalism': 'Professionalität',
    'guarantees.professionalism.desc': 'Klare Kommunikation. Wöchentliche Nachverfolgung. Kontrollierte Versionen.',
    'guarantees.ip': 'Geistiges Eigentum',
    'guarantees.ip.desc': 'Vollständige Übertragung nach Zahlung.',

    // FAQ Section
    'faq.title': 'FAQ',
    'faq.subtitle': 'Antworten auf die häufigsten Fragen zu unseren Dienstleistungen.',
    'faq.q1': 'Wie sind Ihre Tarife?',
    'faq.a1': 'Unsere Tarife sind an Studentenbudgets angepasst: Projektpaket oder zeitbasierte Abrechnung. Wir erstellen vor jeder Mission ein klares und detailliertes Angebot.',
    'faq.q2': 'Wie sind die typischen Fristen?',
    'faq.a2': 'Für ein MVP: 2 bis 6 Wochen je nach Komplexität. Große Projekte werden in Phasen aufgeteilt, um regelmäßige Lieferungen und optimale Überwachung zu gewährleisten.',
    'faq.q3': 'Wer arbeitet an meinem Projekt?',
    'faq.a3': 'Ausgewählte HEPIA-Studenten entsprechend Ihren technischen Anforderungen. Jedes Projekt hat einen Projektmanager und bei Bedarf einen Lehrbetreuer.',
    'faq.q4': 'Behandeln Sie NDAs und geistiges Eigentum?',
    'faq.a4': 'Ja, wir unterzeichnen Standard-Vertraulichkeitsvereinbarungen. Das geistige Eigentum wird nach der finalen Zahlung vollständig an den Kunden übertragen.',
    'faq.q5': 'Bieten Sie Post-Projekt-Support an?',
    'faq.a5': 'Absolut. Wir bieten Wartungsunterstützung und die Möglichkeit neuer Entwicklungsphasen entsprechend Ihren zukünftigen Bedürfnissen.',

    // Contact Section
    'contact.title': 'Starten Sie Ihr Projekt',
    'contact.subtitle': 'Haben Sie ein Projekt im Kopf? Wir werden es diese Woche rahmen.',
    'contact.name': 'Nachname',
    'contact.firstname': 'Vorname',
    'contact.email': 'E-Mail',
    'contact.phone': 'Telefon',
    'contact.organization': 'Organisation',
    'contact.budget': 'Geschätztes Budget',
    'contact.budget.placeholder': 'Wählen Sie Ihr Budget',
    'contact.budget.small': 'Weniger als 2k€',
    'contact.budget.medium': '2k€ - 10k€',
    'contact.budget.large': '10k€ - 25k€',
    'contact.budget.enterprise': 'Mehr als 25k€',
    'contact.budget.discuss': 'Zu besprechen',
    'contact.services.selected': 'Ausgewählte Services',
    'contact.message': 'Nachricht',
    'contact.message.note': '(gewählte Services werden oben hinzugefügt)',
    'contact.send': 'Nachricht senden',
    'contact.info.title': 'Kontaktinformationen',
    'contact.info.email': 'E-Mail',
    'contact.info.address': 'Adresse',
    'contact.social': 'Folgen Sie uns',
    'contact.student.title': 'Sind Sie ein Student?',
    'contact.student.subtitle': 'Treten Sie unserem Beraterteam bei und beteiligen Sie sich an konkreten Projekten.',
    'contact.student.button': 'Als Studentenberater beitreten',

    // Footer
    'footer.description': 'Studentische Experten, die Ihre Ideen in konkrete Ergebnisse verwandeln.',
    'footer.services': 'Services',
    'footer.process': 'Prozess',
    'footer.projects': 'Projekte',
    'footer.contact': 'Kontakt',
    'footer.follow': 'Folgen Sie uns',
    'footer.copyright': 'Junior Enterprise HEPIA. Alle Rechte vorbehalten.',
    'footer.legal': 'Rechtliche Hinweise',
    'footer.privacy': 'Datenschutzrichtlinie',
    'footer.terms': 'AGB',

    // Rejoindre Page
    'join.hero.title': 'Treten Sie unserem Team von Studentenberatern bei',
    'join.hero.subtitle': 'Sind Sie ein HEPIA-Student und möchten Ihre Fähigkeiten in den Dienst konkreter Projekte stellen? Treten Sie uns bei und entwickeln Sie Ihre berufliche Erfahrung.',
    'join.form.title': 'Bewerbungsformular',
    'join.form.subtitle': 'Füllen Sie die untenstehenden Informationen aus, um Ihre Bewerbung einzureichen',
    'join.field': 'Fachbereich / Sektion',
    'join.field.placeholder': 'Wählen Sie Ihren Fachbereich',
    'join.motivation': 'Motivationsschreiben',
    'join.motivation.placeholder': 'Erklären Sie, warum Sie unserem Team beitreten möchten...',
    'join.motivation.min': 'Mindestens 50 Zeichen',
    'join.cv': 'Lebenslauf (nur PDF)',
    'join.cv.upload': 'Laden Sie Ihren Lebenslauf hoch',
    'join.cv.change': 'Klicken Sie, um die Datei zu ändern',
    'join.cv.format': 'Nur PDF-Format, max 5MB',
    'join.privacy.title': 'Datenschutz',
    'join.privacy.text': 'Ihre persönlichen Daten werden gemäß DSGVO verarbeitet. Sie werden nur für Rekrutierungszwecke verwendet und nicht an Dritte weitergegeben. Sie haben das Recht auf Zugang, Berichtigung und Löschung Ihrer Daten.',
    'join.submit': 'Meine Bewerbung einreichen',
    'join.submitting': 'Wird gesendet...',
    'join.success.title': 'Bewerbung erfolgreich gesendet!',
    'join.success.subtitle': 'Vielen Dank für Ihr Interesse, der Junior Enterprise HEPIA beizutreten. Wir werden Ihre Bewerbung prüfen und Sie innerhalb von 72 Stunden kontaktieren.',
    'join.success.button': 'Zurück zur Startseite',

    'join.sections.computer': 'Informatik und Software-Engineering',
    'join.sections.electronics': 'Elektronik',
    'join.sections.microtechnics': 'Mikrotechnik',
    'join.sections.mechanical': 'Maschinenbau',
    'join.sections.architecture': 'Architektur',
    'join.sections.civil': 'Bauingenieurwesen',
    'join.sections.other': 'Anderer Fachbereich',

    // Form validation messages
    'join.validation.section': 'Bitte wählen Sie Ihren Fachbereich.',
    'join.validation.name.min': 'Der Name muss mindestens 2 Zeichen enthalten.',
    'join.validation.firstname.min': 'Der Vorname muss mindestens 2 Zeichen enthalten.',
    'join.validation.phone.min': 'Bitte geben Sie eine gültige Telefonnummer ein.',
    'join.validation.motivation.min': 'Ihr Motivationsschreiben muss mindestens 50 Zeichen enthalten.',
    'join.error.pdf': 'Bitte wählen Sie nur eine PDF-Datei aus.',
    'join.error.size': 'Die Datei darf 5MB nicht überschreiten.',
    'join.error.cv.required': 'Bitte laden Sie Ihren Lebenslauf im PDF-Format hoch.',
    'join.success.prepared': 'Bewerbung vorbereitet!',
    'join.success.email.instruction': 'Ihr E-Mail-Client wird sich öffnen. Vergessen Sie nicht, Ihren Lebenslauf anzuhängen.',
    'join.error.general': 'Ein Fehler ist beim Senden aufgetreten. Bitte versuchen Sie es erneut.',
  }
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('fr')

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['fr']] || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}